/**
 * VoiceProvider.jsx
 * Kisan Mithr Global Voice Assistant Provider.
 * 
 * Hardware-level AudioWorklet + Persistent WebSocket audio streaming architecture.
 * Features:
 * - Real-time 16kHz Linear PCM AudioWorklet streaming
 * - Local Keyword Spotting ("Hey Kisan") on-device
 * - Global Action Router controlling full web application routes & actions
 * - Direct Web Audio API AnalyserNode integration for Three.js 3D visualizer
 * - Survives route transitions across the entire application without unmounting
 * - Global shortcut: Ctrl + Shift + K
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { VoiceContext, VOICE_STATES } from "./VoiceContext";
import { StreamingVoiceClient } from "./StreamingVoiceClient";
import { executeVoiceAction, executeActionPlan } from "./VoiceActionExecutor";
import { matchDeterministicIntent, routeVoiceCommand } from "./IntentRouter";
import { WakeWordEngine } from "./WakeWordEngine";
import { useAuth } from "../context/AuthContext";
import useLanguageStore from "../store/useLanguageStore";

export { VOICE_STATES };


export const VoiceProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { language } = useLanguageStore();

  const [voiceState, setVoiceState] = useState(VOICE_STATES.IDLE);
  const [transcript, setTranscript] = useState("");
  const [partialTranscript, setPartialTranscript] = useState("");
  const [assistantReply, setAssistantReply] = useState("");
  const [volume, setVolume] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastIntent, setLastIntent] = useState(null);
  const [isWidgetExpanded, setIsWidgetExpanded] = useState(false);
  const [isFullscreenMode, setIsFullscreenMode] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);

  const [debugInfo, setDebugInfo] = useState({
    micStatus: "READY",
    lastTranscript: "",
    partialTranscript: "",
    detectedIntent: "NONE",
    confidence: 0,
    source: "NONE",
    executionStatus: "IDLE",
    ttsEngine: "NONE",
    latencyMs: 0
  });

  const clientRef = useRef(null);
  const sessionIdRef = useRef(null);
  const abortControllerRef = useRef(null);
  const streamExecutedMapRef = useRef(new Set());

  // 1. Initialize persistent StreamingVoiceClient
  useEffect(() => {
    const client = new StreamingVoiceClient({
      language: language || "auto",
      onStateChange: (newState) => {
        setVoiceState(newState);
        if (newState === VOICE_STATES.LISTENING || newState === VOICE_STATES.ACTIVATING) {
          setIsWidgetExpanded(true);
          setErrorMessage("");
        } else if (newState === VOICE_STATES.IDLE) {
          setPartialTranscript("");
        }
      },
      onVolumeChange: (vol) => {
        setVolume(vol);
      },
      onSpeechStart: () => {
        setIsWidgetExpanded(true);
        setErrorMessage("");
      },
      onTranscriptPartial: async (partial) => {
        setPartialTranscript(partial);
        setDebugInfo((prev) => ({
          ...prev,
          partialTranscript: partial
        }));

        // ⚡ FLASH SPEED: Instant optimistic command execution on partial transcripts
        const currentSid = clientRef.current?.currentStreamId;
        if (partial && partial.trim() && currentSid && !streamExecutedMapRef.current.has(currentSid)) {
          const cleanPartial = partial.toLowerCase().replace(/^[.,!?;:'"/\\]+|[.,!?;:'"/\\]+$/g, "").trim();
          const isComplete = /^(?:open\s+(?:weather|contact|my\s+field|farm|login|registration|voice\s+assistant|assistant|mandi|soil|schemes|profile|settings|help|home)|scroll\s+(?:down|up)|weather|contact|my\s+field|mandi|soil|schemes)$/i.test(cleanPartial);
          if (isComplete) {
            const fastPlan = matchDeterministicIntent(cleanPartial);
            if (fastPlan && fastPlan.intent !== "UNKNOWN" && fastPlan.actions?.length > 0) {
              console.log("[VoiceProvider] ⚡ FLASH SPEED execution on partial transcript:", fastPlan.intent);
              streamExecutedMapRef.current.add(currentSid);
              setTranscript(partial);
              setVoiceState(VOICE_STATES.EXECUTING);
              clientRef.current?.stopRecordingCommand();
              try {
                const exec = await executeActionPlan(fastPlan, {
                  navigateFn: (path) => {
                    if (path.includes("/assistant")) {
                      clientRef.current?.stopPlayback();
                    }
                    navigate(path);
                  },
                  authContext: auth,
                  isCancelled: () => !sessionIdRef.current
                });
                if (exec.spokenFeedback) {
                  setAssistantReply(exec.spokenFeedback);
                }
                setVoiceState(VOICE_STATES.RESPONDING);
                setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2000);
              } catch (err) {
                console.warn("[VoiceProvider] Optimistic partial action error:", err);
              }
            }
          }
        }
      },
      onTranscriptFinal: async (finalText) => {
        setPartialTranscript("");
        setTranscript(finalText);
        const currentSid = clientRef.current?.currentStreamId;
        if (finalText && finalText.trim()) {
          const fastPlan = matchDeterministicIntent(finalText);
          if (fastPlan && fastPlan.intent === "WAKE_WORD_GREETING") {
            setAssistantReply(fastPlan.spokenFeedback);
            setVoiceState(VOICE_STATES.RESPONDING);
            setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2500);
            return;
          }
          if (fastPlan && fastPlan.intent !== "UNKNOWN" && fastPlan.confidence >= 0.85 && fastPlan.actions?.length > 0) {
            console.log("[VoiceProvider] ⚡ Instant optimistic execution for transcript:", fastPlan.intent);
            if (currentSid) streamExecutedMapRef.current.add(currentSid);
            setVoiceState(VOICE_STATES.EXECUTING);
            try {
              const exec = await executeActionPlan(fastPlan, {
                navigateFn: (path) => {
                  if (path.includes("/assistant")) {
                    clientRef.current?.stopPlayback();
                  }
                  navigate(path);
                },
                authContext: auth,
                isCancelled: () => !sessionIdRef.current
              });
              if (exec.spokenFeedback) {
                setAssistantReply(exec.spokenFeedback);
              }
              setVoiceState(VOICE_STATES.RESPONDING);
              setLastIntent({
                intent: fastPlan.intent,
                actions: fastPlan.actions,
                success: exec.success
              });
              setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2000);
            } catch (err) {
              console.warn("[VoiceProvider] Optimistic action error:", err);
            }
          }
        }
      },
      onResult: async (result) => {
        if (!result) {
          setVoiceState(VOICE_STATES.IDLE);
          return;
        }
        setPartialTranscript("");

        if (!result.success) {
          const errMsg = result.error || "No speech detected.";
          setErrorMessage(errMsg);
          setVoiceState(VOICE_STATES.ERROR);
          setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2000);
          return;
        }

        const currentSid = result.streamId || clientRef.current?.currentStreamId;
        const alreadyExecuted = currentSid && streamExecutedMapRef.current.has(currentSid);

        if (alreadyExecuted) {
          console.log("[VoiceProvider] Stream already executed optimistically; skipping duplicate execution.");
          if (result.spokenFeedback && !result.audioBase64) {
            setAssistantReply(result.spokenFeedback);
            setVoiceState(VOICE_STATES.RESPONDING);
            setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2000);
          }
          return;
        }

        if (result.transcript) {
          setTranscript(result.transcript);

          // Fast local deterministic intent matching first (<5ms)
          const localPlan = matchDeterministicIntent(result.transcript);

          if (localPlan && localPlan.intent !== "UNKNOWN" && localPlan.confidence >= 0.85) {
            setDebugInfo((prev) => ({
              ...prev,
              lastTranscript: result.transcript,
              detectedIntent: localPlan.intent,
              confidence: localPlan.confidence,
              source: localPlan.source || "deterministic_local"
            }));

            // Handle security refusal immediately
            if (localPlan.intent === "SENSITIVE_REFUSAL") {
              setAssistantReply(localPlan.spokenFeedback);
              setVoiceState(VOICE_STATES.RESPONDING);
              setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 3500);
              return;
            }

            // Handle wake word greeting immediately
            if (localPlan.intent === "WAKE_WORD_GREETING") {
              setAssistantReply(localPlan.spokenFeedback);
              setVoiceState(VOICE_STATES.RESPONDING);
              setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2500);
              return;
            }

            // Handle local assistant handoff
            if (localPlan.intent === "LOCAL_ASSISTANT_HANDOFF") {
              setAssistantReply(localPlan.spokenFeedback);
              setVoiceState(VOICE_STATES.RESPONDING);
              return;
            }

            // Execute structured website actions
            if (localPlan.actions && localPlan.actions.length > 0) {
              setVoiceState(VOICE_STATES.EXECUTING);
              try {
                const exec = await executeActionPlan(localPlan, {
                  navigateFn: (path) => {
                    if (path.includes("/assistant")) {
                      clientRef.current?.stopPlayback();
                    }
                    navigate(path);
                  },
                  authContext: auth,
                  isCancelled: () => !sessionIdRef.current
                });

                if (exec.spokenFeedback) {
                  setAssistantReply(exec.spokenFeedback);
                }
                setVoiceState(VOICE_STATES.RESPONDING);

                setLastIntent({
                  intent: localPlan.intent,
                  actions: localPlan.actions,
                  success: exec.success
                });

                if (!result.audioBase64) {
                  setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2000);
                }
                return;
              } catch (execErr) {
                console.error("[VoiceProvider] Local action execution error:", execErr);
              }
            }
          }
        }

        // Server-side fallback feedback & execution
        if (result.spokenFeedback) {
          setAssistantReply(result.spokenFeedback);
          setVoiceState(VOICE_STATES.RESPONDING);
          if (!result.audioBase64) {
            setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2000);
          }
        } else if (!result.audioBase64) {
          setVoiceState(VOICE_STATES.IDLE);
        }

        if (result.error) {
          const userFriendly = result.error.includes("quota")
            ? "Voice service quota has been reached. Please try again later."
            : result.error.includes("busy") || result.error.includes("rate")
            ? "Voice service is temporarily busy. Please try again in a moment."
            : result.error.includes("Microphone") || result.error.includes("Permission")
            ? "Microphone access is required for Hey Kisan. Please allow microphone access and try again."
            : result.error;

          setErrorMessage(userFriendly);
          setVoiceState(VOICE_STATES.ERROR);
          setTimeout(() => {
            setVoiceState(VOICE_STATES.IDLE);
          }, 2500);
        } else {
          setErrorMessage("");
        }

        setDebugInfo((prev) => ({
          ...prev,
          lastTranscript: result.transcript || prev.lastTranscript,
          detectedIntent: result.intent || "NONE",
          confidence: result.confidence || 0,
          source: result.sttEngine || "sarvam_realtime",
          ttsEngine: result.ttsEngine || "None",
          latencyMs: result.latencyMs || 0
        }));

        // Execute action if intent was classified by server
        if (result.intent && result.intent !== "UNKNOWN" && result.intent !== "CONVERSATIONAL_RESPONSE") {
          try {
            const execResult = executeVoiceAction(
              result.intent,
              result.parameters || {},
              auth,
              navigate
            );
            setLastIntent({
              intent: result.intent,
              parameters: result.parameters,
              success: execResult.success
            });
            setDebugInfo((prev) => ({
              ...prev,
              executionStatus: execResult.success ? "SUCCESS" : execResult.reason
            }));
          } catch (execErr) {
            console.error("[VoiceProvider] Failed to execute voice action:", execErr);
          }
        }
      },
      onError: (err) => {
        console.error("[VoiceProvider] Client error:", err);
        const errMsg = err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError"
          ? "Microphone permission is required for voice control. Please allow microphone access and try again."
          : "Microphone access error or audio failed. Please try again.";
        setErrorMessage(errMsg);
        setVoiceState(VOICE_STATES.ERROR);
        setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 3000);
      }
    });

    clientRef.current = client;

    return () => {
      client.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🛡️ Watchdog: Ensure UI NEVER stays frozen in TRANSCRIBING, UNDERSTANDING or EXECUTING (>2.5s)
  useEffect(() => {
    if (voiceState === VOICE_STATES.TRANSCRIBING || voiceState === VOICE_STATES.UNDERSTANDING || voiceState === "UNDERSTANDING" || voiceState === VOICE_STATES.EXECUTING) {
      const timer = setTimeout(() => {
        console.warn(`[VoiceProvider] Safety Watchdog: Auto-recovering from ${voiceState} to IDLE after 2.5s timeout`);
        setVoiceState(VOICE_STATES.IDLE);
        setPartialTranscript("");
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [voiceState]);

  // Update language in client whenever store language changes
  useEffect(() => {
    if (clientRef.current) {
      clientRef.current.language = language || "auto";
    }
  }, [language]);

  // 2. Start Listening for user command
  const startListeningCommand = useCallback(async () => {
    if (!clientRef.current) return;

    // Mutual exclusion check: If on /assistant page, check if local voice assistant is recording
    if (location.pathname === "/assistant") {
      const localRecBtn = document.querySelector("[data-local-mic-active='true']");
      if (localRecBtn) {
        setErrorMessage("Local Voice Assistant is active. Please use one at a time.");
        setVoiceState(VOICE_STATES.ERROR);
        setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 3000);
        return;
      }
    }

    const newSessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    sessionIdRef.current = newSessionId;
    streamExecutedMapRef.current.clear();

    setErrorMessage("");
    setTranscript("");
    setPartialTranscript("");
    setAssistantReply("");
    setIsWidgetExpanded(true);

    const currentUserName = auth?.user?.username || auth?.user?.name || auth?.user?.googleName || "Farmer";
    await clientRef.current.startRecordingCommand({
      language,
      userName: currentUserName
    });
  }, [auth, language, location.pathname]);

  const stopListening = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.stopRecordingCommand();
    }
  }, []);

  // 3. Cancel / Stop Voice
  const cancelVoice = useCallback(() => {
    sessionIdRef.current = null;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (clientRef.current) {
      clientRef.current.cancel();
    }
    setVoiceState(VOICE_STATES.IDLE);
    setPartialTranscript("");
    setErrorMessage("");
  }, []);

  // 4. Global Keyboard Shortcut: Ctrl + Shift + K and Alt + V
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isInputFocused = ["input", "textarea", "select"].includes(activeTag) || document.activeElement?.isContentEditable;

      const isCtrlShiftK = (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "K" || e.key === "k");
      const isAltV = e.altKey && !e.ctrlKey && !e.metaKey && (e.key === "V" || e.key === "v");
      const isEscape = e.key === "Escape";

      if (isEscape && voiceState !== VOICE_STATES.IDLE) {
        e.preventDefault();
        cancelVoice();
        return;
      }

      if (isCtrlShiftK || (isAltV && !isInputFocused)) {
        e.preventDefault();
        setIsWidgetExpanded((prev) => !prev);
        if (voiceState === VOICE_STATES.LISTENING) {
          stopListening();
        } else {
          startListeningCommand();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [voiceState, startListeningCommand, stopListening, cancelVoice]);

  // 5. Direct Text Command Execution
  const executeTextCommand = useCallback(async (commandText) => {
    if (!commandText || !commandText.trim()) return;
    setTranscript(commandText);
    setPartialTranscript("");
    setVoiceState(VOICE_STATES.UNDERSTANDING);
    setIsWidgetExpanded(true);

    try {
      const currentUserName = auth?.user?.username || auth?.user?.name || "Farmer";
      const plan = await routeVoiceCommand(commandText, { userName: currentUserName });

      setDebugInfo((prev) => ({
        ...prev,
        lastTranscript: commandText,
        detectedIntent: plan.intent || "UNKNOWN",
        confidence: plan.confidence || 0,
        source: plan.source || "text_command"
      }));

      if (plan.intent === "SENSITIVE_REFUSAL") {
        setAssistantReply(plan.spokenFeedback);
        setVoiceState(VOICE_STATES.RESPONDING);
        setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 3500);
        return;
      }

      if (plan.intent === "WAKE_WORD_GREETING") {
        setAssistantReply(plan.spokenFeedback);
        setVoiceState(VOICE_STATES.RESPONDING);
        setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2500);
        return;
      }

      if (plan.intent === "LOCAL_ASSISTANT_HANDOFF") {
        setAssistantReply(plan.spokenFeedback);
        setVoiceState(VOICE_STATES.RESPONDING);
        return;
      }

      if (plan.actions && plan.actions.length > 0) {
        setVoiceState(VOICE_STATES.EXECUTING);
        const execResult = await executeActionPlan(plan, { navigateFn: navigate, authContext: auth });
        setLastIntent({
          intent: plan.intent,
          actions: plan.actions,
          success: execResult.success
        });
        setAssistantReply(execResult.spokenFeedback || plan.spokenFeedback || "Done.");
        setVoiceState(VOICE_STATES.RESPONDING);
        setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2500);
      } else if (plan.spokenFeedback) {
        setAssistantReply(plan.spokenFeedback);
        setVoiceState(VOICE_STATES.RESPONDING);
        setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2500);
      } else {
        setAssistantReply("Command not recognized.");
        setVoiceState(VOICE_STATES.RESPONDING);
        setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2000);
      }
    } catch (e) {
      console.error("[VoiceProvider] Text execution error:", e);
      setVoiceState(VOICE_STATES.ERROR);
      setTimeout(() => setVoiceState(VOICE_STATES.IDLE), 2500);
    }
  }, [auth, navigate]);

  const wakeDetectorRef = useRef(null);

  // Initialize and maintain permanent mandatory "Hey Kisan" Wake Word Detector
  useEffect(() => {
    let detector = null;

    const initDetector = async () => {
      try {
        detector = new WakeWordEngine({
          sensitivity: 0.8,
          onWakeWord: (wakeData) => {
            console.log("[VoiceProvider] 🌟 Mandatory Wake Word 'Hey Kisan' spotted!", wakeData);
            setIsWidgetExpanded(true);
            setErrorMessage("");
            setAssistantReply("");

            // If wake phrase included an immediate command in the same breath (e.g. "hey kisan open weather")
            if (wakeData.immediateCommand && wakeData.immediateCommand.trim().length > 1) {
              executeTextCommand(wakeData.immediateCommand.trim());
            } else {
              // Otherwise activate microphone to capture command
              startListeningCommand();
            }
          }
        });

        wakeDetectorRef.current = detector;
        detector.start();

      } catch (err) {
        console.warn("[VoiceProvider] Could not initialize WakeWordEngine:", err);
      }
    };

    initDetector();

    return () => {
      if (detector) {
        detector.destroy();
      }
      wakeDetectorRef.current = null;
    };
  }, [startListeningCommand, executeTextCommand]);

  // Lock wake word detector during active voice interaction or on /assistant to prevent collisions; unlock when IDLE
  useEffect(() => {
    if (!wakeDetectorRef.current) return;

    if (voiceState === VOICE_STATES.IDLE && location.pathname !== "/assistant") {
      wakeDetectorRef.current.unlock();
    } else {
      wakeDetectorRef.current.lock();
    }
  }, [voiceState, location.pathname]);

  const value = {
    voiceState,
    transcript,
    partialTranscript,
    assistantReply,
    volume,
    errorMessage,
    lastIntent,
    isWidgetExpanded,
    setIsWidgetExpanded,
    isFullscreenMode,
    setIsFullscreenMode,
    showDevTools,
    setShowDevTools,
    startListeningCommand,
    stopListening,
    cancelVoice,
    executeTextCommand,
    client: clientRef.current,
    debugInfo
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};

export default VoiceProvider;

